import { RouteObject } from '@rocketlane/rocket-router';
import { FC, lazy } from 'react';
import { PageLoading } from 'components/atoms/loading';
import { useAccountDataQuery } from 'queries/account';
import { NotFound } from 'routes/404/404';
import { SecureRoute } from 'routes/route-with-permission-check';
import { ROUTE_PATHS } from 'routes/routes-paths';
import { NewCustomerPortalPage } from 'routes/vendor/lazy-imports';

const CompanyPageTemplateEditPage = lazy(() =>
  import('./company-pages/edit-page').then((module) => ({
    default: module.CompanyPageTemplateEditPage,
  })),
);
const CompanyPagesTemplatesPage = lazy(() =>
  import('./company-pages/page').then((module) => ({
    default: module.CompanyPagesTemplatesPage,
  })),
);
const SheetTemplatePage = lazy(() =>
  import('components/pages/templates/sheet-template-page').then((module) => ({
    default: module.SheetTemplatePage,
  })),
);

// Inlined here instead of importing `sheetTemplateRoutes` from the page file
// directly — that pre-evaluates `<SheetTemplatePage />` at module load and
// pulls the whole sheets subtree (queries + api + utils) into `app`. With a
// React.lazy wrapper above, the page only loads when the route matches.
const sheetTemplateRoutes: RouteObject = {
  path: '/templates/sheet/:id',
  element: <SheetTemplatePage />,
};
// const EmailPageTemplateEditPage = lazy(() =>
//   import('./email-pages/edit-page').then((module) => ({
//     default: module.EmailPageTemplateEditPage,
//   })),
// );
// const EmailPagesTemplatesPage = lazy(() =>
//   import('./email-pages/page').then((module) => ({
//     default: module.EmailPagesTemplatesPage,
//   })),
// );
const MeetingPageTemplateEditPage = lazy(() =>
  import('./meeting-pages/edit-page').then((module) => ({
    default: module.MeetingPageTemplateEditPage,
  })),
);
const MeetingPagesTemplatesPage = lazy(() =>
  import('./meeting-pages/page').then((module) => ({
    default: module.MeetingPagesTemplatesPage,
  })),
);
// Lazy load all page components
const CpPreviewRoute = lazy(() =>
  import(
    'components/organisms/customer-portal/builder-components/tabs/design-tab/cp-preview-route'
  ).then((module) => ({ default: module.CpPreviewRoute })),
);

const TemplateSheetFieldSectionsPage = lazy(() =>
  import('components/organisms/sheets/template-sheet-field-sections-page').then(
    (module) => ({ default: module.TemplateSheetFieldSectionsPage }),
  ),
);

const DocumentTemplateDetailPage = lazy(() =>
  import('components/organisms/templates/documents/document-detail').then(
    (module) => ({ default: module.DocumentTemplateDetailPage }),
  ),
);

const NewDocumentTemplatePage = lazy(() =>
  import('components/organisms/templates/documents/new-document-detail').then(
    (module) => ({ default: module.NewDocumentTemplatePage }),
  ),
);

const FormTemplateDetailsPage = lazy(() =>
  import('components/organisms/templates/forms/form-details-page').then(
    (module) => ({ default: module.FormTemplateDetailsPage }),
  ),
);

const NewFormTemplatePage = lazy(() =>
  import('components/organisms/templates/forms/new-form-detail').then(
    (module) => ({ default: module.NewFormTemplatePage }),
  ),
);

const NewStatusUpdatesTemplatePage = lazy(() =>
  import('components/organisms/templates/status-updates').then((module) => ({
    default: module.NewStatusUpdatesTemplatePage,
  })),
);

const StatusUpdatesTemplateDetailPage = lazy(() =>
  import('components/organisms/templates/status-updates').then((module) => ({
    default: module.StatusUpdatesTemplateDetailPage,
  })),
);

const CustomerPortalById = lazy(() =>
  import('routes/customer-portal/customer-portal-by-id').then((module) => ({
    default: module.CustomerPortalById,
  })),
);

const CustomerPortalListPage = lazy(() =>
  import('routes/customer-portal/customer-portal-list').then((module) => ({
    default: module.CustomerPortalListPage,
  })),
);

const AiFillTemplatesPage = lazy(() =>
  import('./ai-fill/page').then((module) => ({
    default: module.AiFillTemplatesPage,
  })),
);

const TemplateListRoute = lazy(() =>
  import('./list').then((module) => ({ default: module.TemplateListRoute })),
);

const ProjectTemplateDetailRoute = lazy(() =>
  import('./projects').then((module) => ({
    default: module.ProjectTemplateDetailRoute,
  })),
);

const ProjectTemplateNewRoute = lazy(() =>
  import('./projects').then((module) => ({
    default: module.ProjectTemplateNewRoute,
  })),
);

const ProjectTemplateSettingsRoute = lazy(() =>
  import('./projects/settings').then((module) => ({
    default: module.ProjectTemplateSettingsRoute,
  })),
);

const SavedAutomationsListPage = lazy(() =>
  import('./saved-automations').then((module) => ({
    default: module.SavedAutomationsListPage,
  })),
);

const SavedAutomationById = lazy(() =>
  import('./saved-automations/by-id').then((module) => ({
    default: module.SavedAutomationById,
  })),
);

const TemplatesRoute = lazy(() =>
  import('.').then((module) => ({ default: module.TemplatesRoute })),
);

// Wrapper components that guard routes by their respective feature flags
const CompanyPagesTemplatesPageWrapper: FC = () => {
  const { data: accountData, isLoading } = useAccountDataQuery();
  const isDocumentationAgentEnabled =
    accountData?.accountMeta?.features?.documentationAgent ?? false;

  if (isLoading) {
    return <PageLoading />;
  }

  return isDocumentationAgentEnabled ? (
    <CompanyPagesTemplatesPage />
  ) : (
    <NotFound />
  );
};

const CompanyPageTemplateEditPageWrapper: FC = () => {
  const { data: accountData, isLoading } = useAccountDataQuery();
  const isDocumentationAgentEnabled =
    accountData?.accountMeta?.features?.documentationAgent ?? false;

  if (isLoading) {
    return <PageLoading />;
  }

  return isDocumentationAgentEnabled ? (
    <CompanyPageTemplateEditPage />
  ) : (
    <NotFound />
  );
};

const MeetingPagesTemplatesPageWrapper: FC = () => {
  const { data: accountData, isLoading } = useAccountDataQuery();
  const isMeetingPagesEnabled =
    accountData?.accountMeta?.features?.meetingPages ?? false;

  if (isLoading) {
    return <PageLoading />;
  }

  return isMeetingPagesEnabled ? <MeetingPagesTemplatesPage /> : <NotFound />;
};

const MeetingPageTemplateEditPageWrapper: FC = () => {
  const { data: accountData, isLoading } = useAccountDataQuery();
  const isMeetingPagesEnabled =
    accountData?.accountMeta?.features?.meetingPages ?? false;

  if (isLoading) {
    return <PageLoading />;
  }

  return isMeetingPagesEnabled ? <MeetingPageTemplateEditPage /> : <NotFound />;
};

// const EmailPagesTemplatesPageWrapper: FC = () => {
//   const { data: accountData, isLoading } = useAccountDataQuery();
//   const { hasAccessToResource } = useWithAccess();
//   const isScarletEnabled = accountData?.accountMeta?.features?.scarlet ?? false;
//   const hasEmailPageTemplateRead = hasAccessToResource({
//     rule: 'EMAIL_PAGE_TEMPLATE:READ',
//   });

//   if (isLoading) {
//     return <PageLoading />;
//   }

//   return isScarletEnabled && hasEmailPageTemplateRead ? (
//     <EmailPagesTemplatesPage />
//   ) : (
//     <NotFound />
//   );
// };

// const EmailPageTemplateEditPageWrapper: FC = () => {
//   const { data: accountData, isLoading } = useAccountDataQuery();
//   const { hasAccessToResource } = useWithAccess();
//   const isScarletEnabled = accountData?.accountMeta?.features?.scarlet ?? false;
//   const hasEmailPageTemplateRead = hasAccessToResource({
//     rule: 'EMAIL_PAGE_TEMPLATE:READ',
//   });

//   if (isLoading) {
//     return <PageLoading />;
//   }

//   return isScarletEnabled && hasEmailPageTemplateRead ? (
//     <EmailPageTemplateEditPage />
//   ) : (
//     <NotFound />
//   );
// };

export const templateRoutes: RouteObject = {
  path: ROUTE_PATHS.TEMPLATES,
  element: (
    <SecureRoute rule="TEMPLATE_PAGE:READ">
      <TemplatesRoute />
    </SecureRoute>
  ),
  children: [
    sheetTemplateRoutes,
    {
      path: ROUTE_PATHS.PROJECT_TEMPLATES,
      element: <TemplateListRoute />,
      caseSensitive: true,
    },
    {
      // projects/:templateId/settings
      path: `${ROUTE_PATHS.PROJECT_TEMPLATES}/${ROUTE_PATHS.TEMPLATE_ID}/${ROUTE_PATHS.SETTINGS}/${ROUTE_PATHS.MATCH_ALL}`,
      element: <ProjectTemplateSettingsRoute />,
    },
    {
      path: `${ROUTE_PATHS.PROJECT_TEMPLATES}/${ROUTE_PATHS.NEW}/${ROUTE_PATHS.MATCH_ALL}`,
      element: <ProjectTemplateNewRoute />,
      caseSensitive: true,
    },
    {
      path: `${ROUTE_PATHS.PROJECT_TEMPLATES}/${ROUTE_PATHS.TEMPLATE_ID}/${ROUTE_PATHS.MATCH_ALL}`,
      element: <ProjectTemplateDetailRoute />,
      caseSensitive: true,
    },
    {
      path: ROUTE_PATHS.DOCUMENT_TEMPLATES,
      element: <TemplateListRoute />,
    },
    {
      path: `${ROUTE_PATHS.DOCUMENT_TEMPLATES}/${ROUTE_PATHS.NEW}/${ROUTE_PATHS.MATCH_ALL}`,
      // documents/new/*
      element: <NewDocumentTemplatePage />,
      caseSensitive: true,
    },
    {
      path: `${ROUTE_PATHS.DOCUMENT_TEMPLATES}/${ROUTE_PATHS.TEMPLATE_ID}/${ROUTE_PATHS.MATCH_ALL}`,
      element: <DocumentTemplateDetailPage />,
      caseSensitive: true,
    },

    {
      path: ROUTE_PATHS.STATUS_UPDATE_TEMPLATES,
      element: <TemplateListRoute />,
    },
    {
      path: `${ROUTE_PATHS.STATUS_UPDATE_TEMPLATES}/${ROUTE_PATHS.NEW}/${ROUTE_PATHS.MATCH_ALL}`,
      element: <NewStatusUpdatesTemplatePage />,
      caseSensitive: true,
    },
    {
      path: `${ROUTE_PATHS.STATUS_UPDATE_TEMPLATES}/${ROUTE_PATHS.TEMPLATE_ID}/${ROUTE_PATHS.MATCH_ALL}`,
      element: <StatusUpdatesTemplateDetailPage />,
      caseSensitive: true,
    },

    {
      path: ROUTE_PATHS.FORM_TEMPLATES,
      element: <TemplateListRoute />,
    },
    {
      path: `${ROUTE_PATHS.FORM_TEMPLATES}/${ROUTE_PATHS.NEW}/${ROUTE_PATHS.MATCH_ALL}`,
      element: <NewFormTemplatePage />,
      caseSensitive: true,
    },
    {
      path: `${ROUTE_PATHS.FORM_TEMPLATES}/${ROUTE_PATHS.TEMPLATE_ID}/${ROUTE_PATHS.MATCH_ALL}`,
      element: <FormTemplateDetailsPage />,
      caseSensitive: true,
    },

    {
      path: ROUTE_PATHS.SAVED_AUTOMATIONS,
      element: <SavedAutomationsListPage />,
    },
    {
      path: `${ROUTE_PATHS.SAVED_AUTOMATIONS}/${ROUTE_PATHS.TEMPLATE_ID}/${ROUTE_PATHS.MATCH_ALL}`,
      element: <SavedAutomationById />,
      caseSensitive: true,
    },

    {
      path: `${ROUTE_PATHS.AIFILL_TEMPLATES}`,
      caseSensitive: true,
      element: <AiFillTemplatesPage />,
    },
    {
      path: `${ROUTE_PATHS.COMPANY_PAGES}`,
      caseSensitive: true,
      element: (
        <SecureRoute rule="COMPANY_PAGE_TEMPLATE:READ">
          <CompanyPagesTemplatesPageWrapper />
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.COMPANY_PAGES}/${ROUTE_PATHS.COMPANY_PAGE_ID}`,
      caseSensitive: true,
      element: (
        <SecureRoute rule="COMPANY_PAGE_TEMPLATE:READ">
          <CompanyPageTemplateEditPageWrapper />
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.MEETING_PAGES}`,
      caseSensitive: true,
      element: (
        <SecureRoute rule="MEETING_PAGE_TEMPLATE:READ">
          <MeetingPagesTemplatesPageWrapper />
        </SecureRoute>
      ),
    },
    {
      path: `${ROUTE_PATHS.MEETING_PAGES}/${ROUTE_PATHS.MEETING_PAGE_ID}`,
      caseSensitive: true,
      element: (
        <SecureRoute rule="MEETING_PAGE_TEMPLATE:READ">
          <MeetingPageTemplateEditPageWrapper />
        </SecureRoute>
      ),
    },
    // {
    //   path: `${ROUTE_PATHS.EMAIL_PAGES}`,
    //   caseSensitive: true,
    //   element: <EmailPagesTemplatesPageWrapper />,
    // },
    // {
    //   path: `${ROUTE_PATHS.EMAIL_PAGES}/${ROUTE_PATHS.EMAIL_PAGE_ID}`,
    //   caseSensitive: true,
    //   element: <EmailPageTemplateEditPageWrapper />,
    // },
    {
      path: `${ROUTE_PATHS.CUSTOMER_PORTALS}`,
      caseSensitive: true,
      element: <CustomerPortalListPage />,
    },
    {
      path: `${ROUTE_PATHS.CUSTOMER_PORTALS}/create-theme`,
      caseSensitive: true,
      element: <CustomerPortalListPage />,
    },
    {
      path: `${ROUTE_PATHS.CUSTOMER_PORTALS}/theme-drawer`,
      caseSensitive: true,
      element: <CustomerPortalListPage />,
    },
    {
      path: `${ROUTE_PATHS.CUSTOMER_PORTALS}/${ROUTE_PATHS.NEW}`,
      caseSensitive: true,
      element: <NewCustomerPortalPage />,
    },
    {
      path: `${ROUTE_PATHS.CUSTOMER_PORTALS}/${ROUTE_PATHS.ID}`,
      caseSensitive: true,
      element: <CustomerPortalById />,
    },
    {
      path: `${ROUTE_PATHS.CUSTOMER_PORTALS}/${ROUTE_PATHS.ID}/cp/live-customer-portal-preview/:projectId`,
      caseSensitive: true,
      element: (
        <SecureRoute>
          <CpPreviewRoute />
        </SecureRoute>
      ),
    },
    {
      path: ROUTE_PATHS.SHEET_TEMPLATE,
      element: <TemplateListRoute />,
    },
    {
      path: ROUTE_PATHS.TEMPLATE_SHEET_FIELDS,
      element: <TemplateSheetFieldSectionsPage />,
    },
  ],
};
